import { Component } from '@theme/component';
import { morphSection, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { scrollTo } from '@theme/scroll-container';
import { startViewTransition } from '@theme/utilities';

/**
 * "Show N more" for the catalogue grid.
 *
 * The design asks for an explicit button with a progress bar rather than
 * Horizon's automatic infinite scroll, so this fetches the next page through
 * the Section Rendering API, appends its cards to the grid already on the
 * page, then adopts the fetched control's own state (next URL, progress bar,
 * label) so the button keeps working for the page after that.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [bar] - Progress bar fill.
 * @property {HTMLElement} [label] - "Showing x of y" text.
 * @property {HTMLButtonElement} [button] - The load more button.
 *
 * @extends Component<Refs>
 */
class AgLoadMore extends Component {
  async loadMore() {
    const { nextUrl, sectionId, grid: gridId } = this.dataset;
    if (!nextUrl || !sectionId || this.hasAttribute('loading')) return;

    const grid = gridId ? document.getElementById(gridId) : null;
    if (!grid) return;

    this.setAttribute('loading', '');

    try {
      const html = await sectionRenderer.getSectionHTML(
        sectionId,
        false,
        new URL(nextUrl, window.location.origin)
      );

      const next = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = next.getElementById(gridId ?? '');
      if (!nextGrid) return;

      // Editorial tiles only render on page 1, so everything here is a card.
      grid.append(...Array.from(nextGrid.children));

      const nextControl = next.querySelector('ag-load-more');
      if (nextControl) this.#adopt(nextControl);
      else this.remove();
    } catch (error) {
      console.error('[ag-load-more] Could not load the next page', error);
    } finally {
      this.removeAttribute('loading');
    }
  }

  /**
   * @param {Element} nextControl
   */
  #adopt(nextControl) {
    const nextUrl = nextControl.getAttribute('data-next-url');
    if (nextUrl) this.setAttribute('data-next-url', nextUrl);
    else this.removeAttribute('data-next-url');

    const { bar, label, button } = this.refs;
    const nextBar = nextControl.querySelector('.ag-cat-more__bar');
    const nextLabel = nextControl.querySelector('.ag-cat-more__label');

    if (bar && nextBar instanceof HTMLElement) bar.style.width = nextBar.style.width;
    if (label && nextLabel) label.textContent = nextLabel.textContent;
    if (button && !nextUrl) button.remove();
  }
}

if (!customElements.get('ag-load-more')) {
  customElements.define('ag-load-more', AgLoadMore);
}

/**
 * Category pill row.
 *
 * The row has always scrolled sideways when the categories outgrow the bar,
 * but with the scrollbar hidden that read as a category clipped under the
 * Filters button rather than as something you can move. This adds the missing
 * affordance and nothing else: `data-prev` / `data-next` drive the edge fades
 * and the arrow buttons, and the active pill is pulled into view — on load,
 * and again after a morph switches collection.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [pills] - The scrolling nav.
 *
 * @extends Component<Refs>
 */
class AgPillScroller extends Component {
  /** How much of a pill stays visible past the edge fade. */
  static #EDGE = 24;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #domObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { pills } = this.refs;
    if (!pills) return;

    pills.addEventListener('scroll', this.#sync, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#sync);
    this.#resizeObserver.observe(pills);

    // Switching collection morphs a fresh pill row in, and morph syncs this
    // element's attributes against the server markup — which drops the state
    // below, without calling `updatedCallback` here (measured: morph's
    // onAfterUpdate never reaches this component). Watching the row puts the
    // state back; `toggleAttribute` records no mutation when the value is
    // unchanged, so re-syncing from our own change settles in one pass.
    this.#domObserver = new MutationObserver(this.#onMutate);
    this.#domObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-prev', 'data-next', 'class', 'aria-current'],
      childList: true,
      subtree: true,
    });

    this.#sync();
    this.#revealActive('instant');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.pills?.removeEventListener('scroll', this.#sync);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#domObserver?.disconnect();
    this.#domObserver = null;
  }

  scrollPrev() {
    this.#page(-1);
  }

  scrollNext() {
    this.#page(1);
  }

  /** @param {-1 | 1} direction */
  #page(direction) {
    const { pills } = this.refs;
    if (!pills) return;

    pills.scrollBy({
      left: direction * Math.max(140, pills.clientWidth * 0.8),
      behavior: this.#behavior,
    });
  }

  /**
   * Re-reads the row after anything in it changed. The active pill is only
   * chased when the pills themselves changed — the state attributes below flip
   * on every scroll, and re-centring there would drag the row back under
   * someone scrolling it by hand.
   *
   * @param {MutationRecord[]} records
   */
  #onMutate = (records) => {
    this.#sync();

    const isOwnState = (/** @type {MutationRecord} */ record) =>
      record.type === 'attributes' &&
      record.target === this &&
      (record.attributeName === 'data-prev' || record.attributeName === 'data-next');

    if (records.some((record) => !isOwnState(record))) this.#revealActive();
  };

  /** Reflects how far the row can still travel, for the fades and the arrows. */
  #sync = () => {
    const { pills } = this.refs;
    if (!pills) return;

    const remaining = pills.scrollWidth - pills.clientWidth - pills.scrollLeft;
    this.toggleAttribute('data-prev', pills.scrollLeft > 1);
    this.toggleAttribute('data-next', remaining > 1);
  };

  /**
   * Brings the current category into view, but only when it is not already
   * comfortably inside the row — so an untouched row never jumps.
   *
   * @param {ScrollBehavior} [behavior]
   */
  #revealActive(behavior = this.#behavior) {
    const { pills } = this.refs;
    const active = pills?.querySelector('.ag-pill--dark, [aria-current]');
    if (!pills || !active) return;

    const edge = AgPillScroller.#EDGE;
    const row = pills.getBoundingClientRect();
    const pill = active.getBoundingClientRect();

    let delta = 0;
    if (pill.left < row.left + edge) delta = pill.left - row.left - edge;
    else if (pill.right > row.right - edge) delta = pill.right - row.right + edge;

    if (delta !== 0) pills.scrollBy({ left: delta, behavior });
  }

  get #behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }
}

if (!customElements.get('ag-pill-scroller')) {
  customElements.define('ag-pill-scroller', AgPillScroller);
}

/**
 * The filter panel is pure CSS — a visually hidden checkbox whose `checked`
 * state the server re-renders — so it survives a Section Rendering update with
 * no JavaScript. These delegated listeners add only what CSS cannot: the label
 * answers to Enter/Space, and `aria-expanded` tracks the panel.
 */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const label = target.closest('.ag-cat-filters-btn');
  const id = label?.getAttribute('for');
  const input = id ? document.getElementById(id) : null;
  if (!(input instanceof HTMLInputElement)) return;

  event.preventDefault();
  input.checked = !input.checked;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('ag-cat-toggle')) return;

  for (const label of document.querySelectorAll(`.ag-cat-filters-btn[for="${CSS.escape(input.id)}"]`)) {
    label.setAttribute('aria-expanded', String(input.checked));
  }
});

/* ---------------------------------------------------------------------------
 * Category pills
 *
 * Each pill points at a different collection, so clicking one is a real
 * navigation. Rather than reloading, swap the only two sections that differ —
 * the hero and the catalogue grid — straight from the target page's markup,
 * and push the URL. Everything else (header, footer, cart) is identical
 * between collections, so it stays untouched.
 *
 * If anything about the target page is unexpected, this bails out and lets the
 * browser follow the link normally.
 * ------------------------------------------------------------------------- */

/** Section ids the catalogue swaps, derived from the DOM rather than hardcoded. */
function catalogueSectionIds() {
  const ids = [];
  for (const selector of ['.ag-cat-hero', '.ag-cat']) {
    const wrapper = document.querySelector(selector)?.closest('.shopify-section');
    if (wrapper?.id) ids.push(normalizeSectionId(wrapper.id));
  }
  return ids;
}

/* Skeleton state. Overlapping operations share one counter, and the class is
 * only applied after a short delay so a fast response never flashes it. */
const SKELETON_DELAY = 140;
let busyCount = 0;
/** @type {ReturnType<typeof setTimeout> | null} */
let busyTimer = null;

function beginBusy() {
  busyCount += 1;
  if (busyCount > 1 || busyTimer) return;
  busyTimer = setTimeout(() => {
    busyTimer = null;
    if (busyCount > 0) document.body.classList.add('ag-cat-busy');
  }, SKELETON_DELAY);
}

function endBusy() {
  busyCount = Math.max(0, busyCount - 1);
  if (busyCount > 0) return;
  if (busyTimer) {
    clearTimeout(busyTimer);
    busyTimer = null;
  }
  document.body.classList.remove('ag-cat-busy');
}

// Filtering, sorting, chips and "clear all" all re-render through Horizon's
// facet components, which announce the update with a promise that settles once
// the section has rendered.
document.addEventListener('shopify:collection:update', (event) => {
  if (!document.querySelector('.ag-cat')) return;

  const done = /** @type {{promise?: Promise<unknown>}} */ (/** @type {unknown} */ (event)).promise;
  if (typeof done?.then !== 'function') return;

  beginBusy();
  done.then(endBusy, endBusy);
});

/** @type {AbortController | null} */
let pendingNavigation = null;

/**
 * Replaces the catalogue sections with those of another collection.
 * @param {string} url - The collection URL to show.
 * @param {{push?: boolean, scroll?: boolean}} [options]
 * @returns {Promise<boolean>} false when the caller should fall back to a normal navigation.
 */
async function showCollection(url, { push = true, scroll = true } = {}) {
  const ids = catalogueSectionIds();
  if (ids.length === 0) return false;

  pendingNavigation?.abort();
  const controller = new AbortController();
  pendingNavigation = controller;

  beginBusy();
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    endBusy();
  };

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return false;

    const html = await response.text();
    const next = new DOMParser().parseFromString(html, 'text/html');

    // Only take over when the target really is a catalogue page.
    if (!ids.every((id) => next.getElementById(`shopify-section-${id}`))) return false;

    await startViewTransition(async () => {
      for (const id of ids) await morphSection(id, html);
      if (next.title) document.title = next.title;
      // Drop the skeleton inside the transition, so it cross-fades into the
      // real grid instead of popping in once the animation has finished.
      release();
    }, ['product-grid']);

    if (push) history.pushState({ agCollection: url }, '', url);
    if (scroll) scrollTo({ top: 0, behavior: 'instant' });

    return true;
  } catch (error) {
    // An aborted fetch means a newer click already took over — not a failure.
    if (controller.signal.aborted) return true;
    console.error('[ag-collection] Could not switch collection', error);
    return false;
  } finally {
    if (pendingNavigation === controller) pendingNavigation = null;
    release();
  }
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('.ag-cat-bar__pills a[href]');
  if (!(link instanceof HTMLAnchorElement) || link.target) return;

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return;

  event.preventDefault();

  if (url.pathname === window.location.pathname && url.search === window.location.search) return;

  // Move the active pill straight away so the bar responds to the click;
  // the morph confirms it a moment later.
  for (const pill of link.parentElement?.querySelectorAll('.ag-pill') ?? []) {
    pill.classList.toggle('ag-pill--dark', pill === link);
  }

  showCollection(url.toString()).then((handled) => {
    if (!handled) window.location.assign(url.toString());
  });
});

// Back/forward through category and filter history. Horizon's facet components
// push URLs but never listen for popstate, so this covers those entries too.
window.addEventListener('popstate', () => {
  if (!document.querySelector('.ag-cat')) return;

  showCollection(window.location.href, { push: false, scroll: false }).then((handled) => {
    if (!handled) window.location.reload();
  });
});
